import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'

/**
 * Checks if an element is whitespace-only and followed by FieldRef or colon-starting element.
 * Used to skip unnecessary whitespace in keyed text concatenation.
 */
const isWhitespaceBeforeColonOrFieldRef = (
    elements: Partial<TextContentElement>[],
    index: number
): boolean => {
    const current = elements[index]
    if (current.type !== 'Text' || current.text?.trim() !== '') {
        return false
    }

    const next = elements[index + 1]
    if (!next) return false

    return next.type === 'FieldRef' || !!next.text?.trimStart().startsWith(':')
}

/**
 * Checks if the next element is a FieldRef or starts with a colon.
 * Used to determine if trailing whitespace should be trimmed from the current element.
 */
const isNextElementFieldRefOrColon = (
    elements: Partial<TextContentElement>[],
    index: number
): boolean => {
    const next = elements[index + 1]
    if (!next) return false

    return next.type === 'FieldRef' || !!next.text?.trimStart().startsWith(':')
}

/**
 * Concatenates text from elements in a content item up to and including the colon.
 *
 * This function is used to extract the full key portion of keyed text, which may be
 * split across multiple elements in the content item's elements array.
 *
 * @param contentItem - The content item containing text elements
 * @returns The concatenated text up to and including the colon, or the top-level text if no elements
 */
export const getConcatenatedText = (
    contentItem: Partial<TextContentElement>
) => {
    // If no elements array or empty, fallback to top-level text
    if (!contentItem?.elements || contentItem.elements.length === 0) {
        return contentItem?.text || ''
    }

    // Concatenate element texts up to and including the element with colon
    const textsUpToColon: string[] = []
    for (let i = 0; i < contentItem.elements.length; i++) {
        const el = contentItem.elements[i]
        let elementText = el.text || ''

        // Skip whitespace before FieldRef or colon-starting element
        if (isWhitespaceBeforeColonOrFieldRef(contentItem.elements, i)) {
            continue
        }

        // Trim trailing whitespace if next element is FieldRef or colon
        if (isNextElementFieldRefOrColon(contentItem.elements, i)) {
            elementText = elementText.trimEnd()
        }

        textsUpToColon.push(elementText)

        // Stop after finding the colon
        if (elementText.includes(':')) {
            break
        }
    }

    return textsUpToColon.join('')
}

/**
 * Checks if an element should be skipped to fix ProductType key:colon spacing issues.
 *
 * Skips whitespace before FieldRef/colon (ProductType only)
 * Pattern: " " followed by FieldRef or ":"
 * Prevents: "Key : Value" instead of "Key: Value"
 *
 * Note: We do NOT skip the colon element after FieldRef because FieldRef has empty text
 * and doesn't render a colon - the colon element IS the actual colon to display.
 *
 * @returns true if element should be skipped
 */
export const shouldSkipForKeyColonSpacing = (
    currentItem: Partial<TextContentElement>,
    index: number,
    contentItemNodes: Partial<TextContentElement>[],
    isProductType: boolean
): boolean => {
    if (currentItem.type !== 'Text') {
        return false
    }

    const trimmedText = currentItem.text?.trim() ?? ''

    // Skip whitespace before FieldRef/colon (ProductType only)
    if (isProductType && trimmedText === '') {
        const nextItem = contentItemNodes[index + 1]
        if (nextItem?.type === 'FieldRef') {
            return true
        }
        if (
            nextItem?.type === 'Text' &&
            nextItem.text?.trimStart().startsWith(':')
        ) {
            return true
        }
    }

    return false
}
